import { Coordinate2D, Point3D } from '../components/viewer/2D/Elements'
import { DrawingFile } from './File'
import {
  Beam,
  Header,
  LoadBearingWall,
  PileCap,
  RectangleFramingElement,
  Ridge,
  ShearWall,
} from './Structural'

export enum BuildingElementType {
  Floor = 'Floor',
  Wall = 'Wall',
  Window = 'Window',
  Door = 'Door',
  Roof = 'Roof',
  Storey = 'Storey',
  Room = 'Room',
  Structural = 'Structural',
  Sketch = 'Sketch',
  Span = 'Span',
  Utility = 'Utility',
  WallGap = 'WallGap',
  BimObject = 'BimObject',
}

export enum BuildingElementSubType {
  Exterior = 'EXTERIOR',
  Interior = 'INTERIOR',
  Site = 'SITE',
  Unknown = 'UNKNOWN',
}

export enum BuildingStructuralWallType {
  Sheer = 'Sheer',
  LoadBearing = 'Load Bearing',
}

export enum BuildingOpeningSize {
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
}

export enum BuildingBeamSize {
  Size1 = '4x6',
  Size2 = '4x8',
  Size3 = '4x10',
  Size4 = '4x12',
}

export const BuildingWindowSizes: Record<
  BuildingOpeningSize,
  { width: number; height: number }
> = {
  [BuildingOpeningSize.Small]: {
    width: 24,
    height: 60,
  },
  [BuildingOpeningSize.Medium]: {
    width: 48,
    height: 60,
  },
  [BuildingOpeningSize.Large]: {
    width: 72,
    height: 60,
  },
}

export const BuildingDoorSizes: Record<
  BuildingOpeningSize,
  { width: number; height: number }
> = {
  [BuildingOpeningSize.Small]: {
    width: 24,
    height: 72,
  },
  [BuildingOpeningSize.Medium]: {
    width: 36,
    height: 72,
  },
  [BuildingOpeningSize.Large]: {
    width: 48,
    height: 72,
  },
}

export const BuildingBeamSizes: Record<
  BuildingBeamSize,
  { width: number; thickness: number }
> = {
  [BuildingBeamSize.Size1]: {
    thickness: 3.5,
    width: 5.5,
  },
  [BuildingBeamSize.Size2]: {
    thickness: 3.5,
    width: 7.5,
  },
  [BuildingBeamSize.Size3]: {
    thickness: 3.5,
    width: 9.25,
  },
  [BuildingBeamSize.Size4]: {
    thickness: 3.5,
    width: 11.25,
  },
}

export enum BuildingElementStatus {
  NotStarted,
  Running,
  Complete,
}

export enum BuildingViewMode {
  SingleStorey,
  StackableView,
  WholeBuilding,
}

const buildingElementTypes = [
  BuildingElementType.Floor,
  BuildingElementType.Wall,
  BuildingElementType.Window,
  BuildingElementType.Door,
]
export default buildingElementTypes

export enum StoreyType {
  Ground = 0,
  Middle = 1,
  Top = 2,
}

export interface BimObjectRef {
  bimobject_ref?: string
}

export interface ConvertData {
  building: Building
  json_s3_key: string
  context_id: string
  message: string
}

export interface Building {
  storeys: Storey[]
  roofs: Roof[]
  stairs: Stairs[]
  foundations: Foundation[]
  has_elements: BuildingElementsStatus
  has_structural: boolean
  floor_area: number
  lot_area: number
}

export interface Storey {
  id: string
  name: string
  height: number
  type: string
  poly_points: number[][]
  rooms: Room[]
  walls: Wall[]
  selected_shear_walls: ShearWall[]
  optional_shear_walls: ShearWall[]
  load_bearing_walls: LoadBearingWall[]
  non_bearing_walls: LoadBearingWall[]
  beams: Beam[]
  spans: Span[]

  // UI related properties
  image: string
  isUploading: boolean
}

export interface Foundation {
  id: string
  top_elevation: number
  beams: Beam[]
  pile_caps: PileCap[]
}

export interface Span {
  id: string
  joists: RectangleFramingElement[]
}

export interface Roof {
  id: string
  storey_id: string
  surfaces: Surface[]
  ridges: Ridge[]
  hips: RectangleFramingElement[]
}

export interface Stairs {
  id: string
  surfaces: Polygon[]
}

export interface BuildingElementsStatus {
  exterior_walls: boolean
  interior_walls: boolean
  doors: boolean
  windows: boolean
  rooms: boolean
  roof: boolean
}

export interface Room {
  id: string
  name: string
  storey_id: string
  walls: string[]
  floor: Floor
}

export interface Floor {
  id: string
  elevation: number
  poly_points: number[][]
}

export interface Wall {
  id: string
  type: BuildingElementSubType
  length: number
  height: number
  slope: number
  thickness: number
  angle: number
  bottom_elevation: number
  center_point: number[]
  rooms: string[]
  windows: Window[]
  doors: Door[]
  studs: RectangleFramingElement[]
}

export interface Window extends BimObjectRef {
  id: string
  wall_id: string
  type: string
  width: number
  height: number
  thickness: number
  center_location_on_wall: number
  sill_height: number
  headers: Header[]
  studs: RectangleFramingElement[]
}

export interface Door extends BimObjectRef {
  id: string
  width: number
  height: number
  thickness: number
  type: string
  wall_id: string
  sill_height: number
  hinge_side: string
  center_location_on_wall: number
  headers: Header[]
  studs: RectangleFramingElement[]
}

export interface FloorData {
  id: string
  elevation: number
  poly_points: Coordinate2D[]
}

export interface Window2D {
  id: string
  type: string
  width: number
  height: number
  thickness: number
  center_location_on_wall: number
  sill_height: number
  wall_id?: string
  headers?: Header2D[]
  studs?: Stud2D[]
  bot_cripples?: any[]
  top_cripples?: any[]
  sill_plate?: any
  post_ids?: string[]
}

export interface Door2D {
  id: string
  type: string
  width: number
  height: number
  thickness: number
  center_location_on_wall: number
  sill_height: number
  hinge_side?: 'left' | 'right'
  wall_id?: string
  headers?: any[]
  studs?: any[]
  bot_cripples?: any[]
  top_cripples?: any[]
  sill_plate?: any
  post_ids?: string[]
}

export interface Wall2D {
  id: string
  angle: number
  bottom_elevation?: number
  center_point: Coordinate2D
  height: number
  length: number
  thickness: number
  type?: string
  windows?: Window2D[]
  doors?: Door2D[]
  rooms?: string[]
  slope?: number
  top_plates?: any[]
  bot_plate?: {}
  post_ids?: string[]
  wall_id?: string
  stiffness_plf?: number
  force?: number
  is_facade?: boolean
  stiffness?: number
}

export interface Room2D {
  id: string
  floor: FloorData
  name: string
  storey_id: string
  type: 'LIVING_ROOM' | 'UNKNOWN' | 'LINEN' | 'CLOSET'
  classification: 'HABITABLE' | 'UNKNOWN' | 'STORAGE'
  walls: string[]
}

export interface Beam2D {
  id: string
  center_point: Point3D
  length: number
  rotation_x: number
  rotation_y: number
  rotation_z: number
  thickness: number
  width: number
  posts?: Post2D[]
  type: string // 'Beam' | 'Joist' | 'Header' | etc.
}

export interface Joist2D {
  id: string
  center_point: Point3D
  length: number
  rotation_x: number
  rotation_y: number
  rotation_z: number
  thickness: number
  width: number
  type: string // 'Joist'
}

export interface Span2D {
  id: string
  joists: Joist2D[]
}

export interface Post2D {
  id: string
  center_point: Point3D
  length: number
  rotation_x: number
  rotation_y: number
  rotation_z: number
  thickness: number
  width: number
  z_reference?: string
  type: string // 'Post'
}

export interface Stud2D {
  id: string
  center_point: Point3D
  length: number
  rotation_x: number
  rotation_y: number
  rotation_z: number
  thickness: number
  width: number
  posts?: Post2D[]
  type: string // 'Stud'
}

export interface Header2D {
  id: string
  center_point: Point3D
  length: number
  rotation_x: number
  rotation_y: number
  rotation_z: number
  thickness: number
  width: number
  posts?: Post2D[]
  type: string // 'Header'
}

export interface ElementsData {
  id: string
  height: number
  name: string
  type?: string
  rooms?: Room2D[]
  walls?: Wall2D[]
  beams?: Beam2D[]
  spans?: Span2D[]
  posts?: Post2D[]
  poly_points?: Coordinate2D[]
  load_bearing_walls?: Wall2D[]
  non_bearing_walls?: Wall2D[]
  optional_shear_walls?: Wall2D[]
  selected_shear_walls?: Wall2D[]
  strips?: any[]
}

export interface BaseElementData {
  id: string
  type: string
  center_point?: [number, number] | { x: number; y: number; z?: number }
  angle?: number
  [key: string]: any
}

export interface BaseElementBounds {
  centerX: number
  centerY: number
  width: number
  height: number
  angle: number
}

export interface Vertex2D {
  x: number
  y: number
}

export interface Vertex3D {
  x: number
  y: number
  z: number
}

export interface Polygon {
  nodes: Node[]
  edges: Edge[]
  object_id: string
  slope: number
}

export interface Surface {
  id: string
  polygon: Polygon
  rafters_spacing: number
  rafters: RectangleFramingElement[]
}

export interface Node {
  point: Vertex3D
  edges_ids: string[]
}

export interface Edge {
  id: string
  node1: Node
  node2: Node
}

export enum BuildingEditingTool {
  DirectSelect = 'DirectSelect',
  Marquee = 'Marquee',
  Measure = 'Measure',
  Annotation = 'Annotation',
  Flip = 'Flip',
  Grid = 'Grid',
  Comment = 'Comment',
}

export enum BuildingObjectTool {
  Walls = 'Walls',
  Doors = 'Doors',
  Windows = 'Windows',
  Framing = 'Framing',
  Stairs = 'Stairs',
  Ventilation = 'Ventilation',
  Electrical = 'Electrical',
  Plumbing = 'Plumbing',
  Foundation = 'Foundation',
}

export enum BuildingStructuralObjectTool {
  Beams = 'Beams',
}

export type BuildingTool =
  | BuildingEditingTool
  | BuildingObjectTool
  | BuildingStructuralObjectTool

export interface ConvertFailedResponse {
  message: string
  details: {
    [key: number]: ConvertIssue[]
  }
}

export interface ConvertIssue {
  name: string
  image: string
}

export interface ConvertOverrides {
  best_splits?: {
    [key: number]: string
  }
}

export enum BuildingDocumentType {
  Floorplans2D = '2d_floorplans',
  Permit = 'permit',
  Report = 'calculation_book',
  Structural = 'structural',
}

export interface BuildingDocument {
  file: DrawingFile
  type: BuildingDocumentType
  selectedPageId: string | null
}

export interface GridSettings {
  color: string
  cellSize: number
  cellFrequency: number
  snapToGrid: boolean
}

export const defaultGridSettings: GridSettings = {
  color: '#1D1C1F',
  cellSize: 5,
  cellFrequency: 10,
  snapToGrid: true,
}

export enum HideShowOption {
  RoofsVisibility = 'Roofs',
  WallsTransparency = 'Walls',
  RoomNamesVisibility = 'Room Names',
  RulersVisibility = 'Rulers',
  MeasurementsVisibility = 'Measurements',
  SqFtVisibility = 'SqFt',
  GridVisibility = 'Grid',
}

export const defaultHideShowOptions: Record<HideShowOption, boolean> = {
  [HideShowOption.RoofsVisibility]: true,
  [HideShowOption.WallsTransparency]: false,
  [HideShowOption.RoomNamesVisibility]: true,
  [HideShowOption.RulersVisibility]: true,
  [HideShowOption.MeasurementsVisibility]: true,
  [HideShowOption.SqFtVisibility]: true,
  [HideShowOption.GridVisibility]: true,
}
